import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { Category } from "../../../api";
import { usePermissions } from "../../../hooks";
import { ACTIONS, ENV } from "../../../utils/constants";

import styles from "./Category.module.scss";

const LIMIT_OPTIONS = [10, 20, 50];

function getTreeResponse(result) {
  const response = result?.categories || result?.response || result || {};

  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response.docs)) {
    return response.docs;
  }

  return [];
}

function getCategoryId(category) {
  return String(category?._id || category?.id || "");
}

function getParentId(parent) {
  if (!parent) {
    return null;
  }

  if (typeof parent === "object") {
    return parent._id || parent.id || null;
  }

  return parent;
}

function flattenCategories(categories, parentId = null, level = 1) {
  const result = [];

  categories.forEach((category) => {
    const normalizedCategory = {
      ...category,
      parent: getParentId(category.parent) || parentId,
      level: Number(category.level) || level,
      children: Array.isArray(category.children) ? category.children : [],
    };

    result.push(normalizedCategory);

    if (normalizedCategory.children.length > 0) {
      result.push(
        ...flattenCategories(
          normalizedCategory.children,
          normalizedCategory._id,
          normalizedCategory.level + 1
        )
      );
    }
  });

  return result;
}

function getAllCategoryIds(categories) {
  return flattenCategories(categories)
    .map((category) => getCategoryId(category))
    .filter(Boolean);
}

function getCategoryProductCount(category) {
  if (typeof category.productsCount === "number") {
    return category.productsCount;
  }

  if (typeof category.productCount === "number") {
    return category.productCount;
  }

  if (typeof category.products === "number") {
    return category.products;
  }

  if (Array.isArray(category.products)) {
    return category.products.length;
  }

  return 0;
}

function getCategoryImage(category) {
  const imagePath = category?.image?.path;

  if (!imagePath) {
    return "";
  }

  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
    return imagePath;
  }

  const normalizedPath = imagePath.replace(/\\/g, "/").replace(/^\/+/, "");

  return `${ENV.SERVER_HOST}/${normalizedPath}`;
}

function getVisibleCategories(categories, expandedIds, searchValue) {
  const normalizedSearch = searchValue.trim().toLowerCase();

  function categoryMatchesSearch(category) {
    if (!normalizedSearch) {
      return true;
    }

    return [
      category.id,
      category.name,
      category.slug,
      category.description,
    ]
      .filter(Boolean)
      .some((value) =>
        String(value).toLowerCase().includes(normalizedSearch)
      );
  }

  function hasMatchingDescendant(category) {
    const children = Array.isArray(category.children)
      ? category.children
      : [];

    return children.some(
      (child) =>
        categoryMatchesSearch(child) || hasMatchingDescendant(child)
    );
  }

  function walk(items, inheritedLevel = 1) {
    const rows = [];

    items.forEach((category) => {
      const children = Array.isArray(category.children)
        ? category.children
        : [];

      const categoryLevel = Number(category.level) || inheritedLevel;
      const matchesSearch =
        categoryMatchesSearch(category) || hasMatchingDescendant(category);

      if (normalizedSearch && !matchesSearch) {
        return;
      }

      const normalizedCategory = {
        ...category,
        level: categoryLevel,
        children,
      };

      rows.push(normalizedCategory);

      const isExpanded =
        Boolean(normalizedSearch) ||
        expandedIds.includes(getCategoryId(normalizedCategory));

      if (children.length > 0 && isExpanded) {
        rows.push(...walk(children, categoryLevel + 1));
      }
    });

    return rows;
  }

  return walk(categories);
}

function getCategoryPayload(category, visible) {
  return {
    id: category.id,
    name: category.name,
    slug: category.slug,
    parent: getParentId(category.parent),
    description: category.description || "",
    order: category.order || 1,
    visible,
    seo: {
      metaTitle: category.seo?.metaTitle || "",
      metaDescription: category.seo?.metaDescription || "",
    },
  };
}

export function CategoryPage() {
  const { hasAction } = usePermissions();
  const canAdd = hasAction("categories", ACTIONS.ADD);
  const canEdit = hasAction("categories", ACTIONS.MOD);
  const canDelete = hasAction("categories", ACTIONS.DEL);

  const location = useLocation();
  const actionMenuRef = useRef(null);

  const [categoryTree, setCategoryTree] = useState([]);
  const [expandedIds, setExpandedIds] = useState([]);
  const [openActionMenu, setOpenActionMenu] = useState(null);

  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [isUpdatingVisibility, setIsUpdatingVisibility] = useState(false);
  const [updatingCategoryId, setUpdatingCategoryId] = useState(null);

  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const [limit, setLimit] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  const [successMessage, setSuccessMessage] = useState(
    location.state?.success || ""
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const loadCategories = useCallback(async () => {
    try {
      setIsLoading(true);
      setError("");
      setOpenActionMenu(null);

      const categoryController = new Category();
      const result = await categoryController.getCategoryTree();
      const categories = getTreeResponse(result);

      setCategoryTree(categories);
      setExpandedIds(getAllCategoryIds(categories));
    } catch (errorResponse) {
      console.error("loadCategories error:", errorResponse);

      setCategoryTree([]);
      setExpandedIds([]);

      setError(
        errorResponse?.msg ||
          "No fue posible cargar las categorías. Intenta nuevamente."
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    if (!successMessage) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      setSuccessMessage("");
    }, 4000);

    return () => window.clearTimeout(timeout);
  }, [successMessage]);

  useEffect(() => {
    function handlePointerDown(event) {
      if (
        actionMenuRef.current &&
        !actionMenuRef.current.contains(event.target)
      ) {
        setOpenActionMenu(null);
      }
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setOpenActionMenu(null);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const allCategories = useMemo(
    () => flattenCategories(categoryTree),
    [categoryTree]
  );

  const visibleCategories = useMemo(() => {
    return getVisibleCategories(categoryTree, expandedIds, appliedSearch);
  }, [categoryTree, expandedIds, appliedSearch]);

  const totalPages = Math.max(
    1,
    Math.ceil(visibleCategories.length / limit)
  );

  const paginatedCategories = useMemo(() => {
    const firstIndex = (currentPage - 1) * limit;

    return visibleCategories.slice(firstIndex, firstIndex + limit);
  }, [visibleCategories, currentPage, limit]);

  const firstVisibleItem =
    visibleCategories.length === 0 ? 0 : (currentPage - 1) * limit + 1;

  const lastVisibleItem = Math.min(
    currentPage * limit,
    visibleCategories.length
  );

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [appliedSearch, limit]);

  function handleSearchSubmit(event) {
    event.preventDefault();
    setAppliedSearch(searchValue);
    setOpenActionMenu(null);
  }

  function handleClearSearch() {
    setSearchValue("");
    setAppliedSearch("");
  }

  function handleToggleExpanded(categoryId) {
    setExpandedIds((currentIds) =>
      currentIds.includes(categoryId)
        ? currentIds.filter((id) => id !== categoryId)
        : [...currentIds, categoryId]
    );
  }

  function handleExpandAll() {
    setExpandedIds(getAllCategoryIds(categoryTree));
  }

  function handleCollapseAll() {
    setExpandedIds([]);
  }

  function handleOpenDeleteModal(category) {
    setOpenActionMenu(null);
    setCategoryToDelete(category);
    setError("");
  }

  function handleCloseDeleteModal() {
    if (!isDeleting) {
      setCategoryToDelete(null);
    }
  }

  async function handleConfirmDelete() {
    if (!categoryToDelete) {
      return;
    }

    try {
      setIsDeleting(true);
      setError("");

      const categoryController = new Category();

      await categoryController.deleteCategory(categoryToDelete._id);

      setSuccessMessage("Categoría archivada correctamente.");
      setCategoryToDelete(null);

      await loadCategories();
    } catch (errorResponse) {
      console.error("handleConfirmDelete error:", errorResponse);

      setError(
        errorResponse?.msg ||
          "No fue posible archivar la categoría. Intenta nuevamente."
      );
    } finally {
      setIsDeleting(false);
    }
  }

  async function handleChangeVisibility(category) {
    if (isUpdatingVisibility) {
      return;
    }

    try {
      setIsUpdatingVisibility(true);
      setUpdatingCategoryId(category._id);
      setError("");

      const categoryController = new Category();

      await categoryController.updateCategory(
        category._id,
        getCategoryPayload(category, !category.visible)
      );

      setSuccessMessage(
        category.visible
          ? "Categoría oculta de la tienda."
          : "Categoría visible en la tienda."
      );

      await loadCategories();
    } catch (errorResponse) {
      console.error("handleChangeVisibility error:", errorResponse);

      setError(
        errorResponse?.msg ||
          "No fue posible actualizar la visibilidad de la categoría."
      );
    } finally {
      setIsUpdatingVisibility(false);
      setUpdatingCategoryId(null);
    }
  }

  return (
    <section className={styles.CategoryPage}>
      <div className={styles.TopBar}>
        <form className={styles.SearchForm} onSubmit={handleSearchSubmit}>
          <input
            type="search"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            placeholder="Buscar categorías..."
            aria-label="Buscar categorías"
          />

          <button type="submit" aria-label="Buscar categorías">
            ⌕
          </button>
        </form>

        <div className={styles.TopActions}>
          {canAdd && (
            <Link
              to="/admin/categories/create"
              className={styles.CreateButton}
            >
              <span>＋</span>
              Nueva categoría
            </Link>
          )}
        </div>
      </div>

      <div className={styles.FilterBar}>
        <div className={styles.TreeActions}>
          <button
            type="button"
            className={styles.TreeButton}
            onClick={handleExpandAll}
          >
            Expandir todo
          </button>

          <button
            type="button"
            className={styles.TreeButton}
            onClick={handleCollapseAll}
          >
            Contraer todo
          </button>
        </div>
      </div>

      {appliedSearch.trim() && (
        <div className={styles.Filters}>
          <span className={styles.ActiveSearch}>
            Búsqueda: <strong>{appliedSearch}</strong>
          </span>

          <button
            type="button"
            className={styles.ClearButton}
            onClick={handleClearSearch}
          >
            Limpiar búsqueda
          </button>
        </div>
      )}

      {successMessage && (
        <div className={styles.SuccessMessage} role="status">
          {successMessage}
        </div>
      )}

      {error && (
        <div className={styles.ErrorMessage} role="alert">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className={styles.StateMessage}>Cargando categorías...</div>
      ) : categoryTree.length === 0 ? (
        <div className={styles.EmptyState}>
          <p>No hay categorías para mostrar.</p>
        </div>
      ) : visibleCategories.length === 0 ? (
        <div className={styles.EmptyState}>
          <p>No se encontraron categorías con esa búsqueda.</p>

          <button
            type="button"
            className={styles.ClearButton}
            onClick={handleClearSearch}
          >
            Limpiar búsqueda
          </button>
        </div>
      ) : (
        <>
          <div className={styles.TableContainer}>
            <table className={styles.Table}>
              <thead>
                <tr>
                  <th>Categoría</th>
                  <th>Productos</th>
                  <th>Orden</th>
                  <th>Visible</th>
                  <th className={styles.ActionsColumn}>Acciones</th>
                </tr>
              </thead>

              <tbody>
                {paginatedCategories.map((category) => {
                  const categoryId = getCategoryId(category);
                  const hasChildren = category.children.length > 0;
                  const isExpanded = expandedIds.includes(categoryId);
                  const categoryImage = getCategoryImage(category);

                  return (
                    <tr key={categoryId}>
                      <td>
                        <div
                          className={styles.CategoryCell}
                          style={{
                            "--category-level": Math.max(
                              (category.level || 1) - 1,
                              0
                            ),
                          }}
                        >
                          {hasChildren ? (
                            <button
                              type="button"
                              className={styles.ExpandButton}
                              onClick={() =>
                                handleToggleExpanded(categoryId)
                              }
                              aria-label={
                                isExpanded
                                  ? `Contraer ${category.name}`
                                  : `Expandir ${category.name}`
                              }
                            >
                              {isExpanded ? "⌄" : "›"}
                            </button>
                          ) : (
                            <span className={styles.EmptyExpandSpace} />
                          )}

                          {categoryImage ? (
                            <img
                              className={styles.CategoryImage}
                              src={categoryImage}
                              alt=""
                            />
                          ) : (
                            <span className={styles.CategoryIcon}>
                              {hasChildren ? "▣" : "▪"}
                            </span>
                          )}

                          <div>
                            <p
                              className={styles.CategoryName}
                              title={category.name}
                            >
                              {category.name}
                            </p>

                            {category.slug && (
                              <span
                                className={styles.CategorySlug}
                                title={category.slug}
                              >
                                {category.slug}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      <td>
                        <span className={styles.ProductCount}>
                          {getCategoryProductCount(category)}
                        </span>
                      </td>

                      <td>
                        <span className={styles.OrderBadge}>
                          {category.order}
                        </span>
                      </td>

                      <td>
                        <label
                          className={styles.VisibilitySwitch}
                          title={
                            category.visible
                              ? "Ocultar de la tienda"
                              : "Mostrar en la tienda"
                          }
                        >
                          <input
                            type="checkbox"
                            checked={Boolean(category.visible)}
                            onChange={() =>
                              handleChangeVisibility(category)
                            }
                            disabled={
                              !canEdit ||
                              (isUpdatingVisibility &&
                                updatingCategoryId === categoryId)
                            }
                          />

                          <span />
                        </label>
                      </td>

                      <td className={styles.ActionsColumn}>
                        {(canEdit || canDelete) && (
                        <div
                          ref={
                            openActionMenu === categoryId
                              ? actionMenuRef
                              : null
                          }
                          className={styles.RowActions}
                        >
                          <button
                            type="button"
                            className={styles.MenuButton}
                            aria-label={`Acciones para ${category.name}`}
                            aria-expanded={openActionMenu === categoryId}
                            aria-haspopup="menu"
                            onClick={() =>
                              setOpenActionMenu((currentId) =>
                                currentId === categoryId
                                  ? null
                                  : categoryId
                              )
                            }
                          >
                            ⋮
                          </button>

                          {openActionMenu === categoryId && (
                            <div
                              className={styles.ActionMenu}
                              role="menu"
                            >
                              {canEdit && (
                              <Link
                                to={`/admin/categories/${categoryId}/edit`}
                                role="menuitem"
                                onClick={() => setOpenActionMenu(null)}
                              >
                                Editar categoría
                              </Link>
                              )}

                              {canDelete && (
                              <button
                                type="button"
                                className={styles.DeleteAction}
                                role="menuitem"
                                onClick={() =>
                                  handleOpenDeleteModal(category)
                                }
                              >
                                Archivar categoría
                              </button>
                              )}
                            </div>
                          )}
                        </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <footer className={styles.Pagination}>
            <span className={styles.TableInfo}>
              Mostrando {firstVisibleItem} a {lastVisibleItem} de{" "}
              {visibleCategories.length} categorías
            </span>

            <div className={styles.PageControls}>
              <button
                type="button"
                className={styles.PageButton}
                onClick={() =>
                  setCurrentPage((page) => Math.max(page - 1, 1))
                }
                disabled={currentPage === 1}
                aria-label="Página anterior"
              >
                ‹
              </button>

              <span className={styles.CurrentPage}>
                {currentPage} de {totalPages}
              </span>

              <button
                type="button"
                className={styles.PageButton}
                onClick={() =>
                  setCurrentPage((page) => Math.min(page + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                aria-label="Página siguiente"
              >
                ›
              </button>
            </div>

            <label className={styles.LimitControl}>
              <select
                value={limit}
                onChange={(event) => setLimit(Number(event.target.value))}
              >
                {LIMIT_OPTIONS.map((optionLimit) => (
                  <option key={optionLimit} value={optionLimit}>
                    {optionLimit} por página
                  </option>
                ))}
              </select>
            </label>
          </footer>
        </>
      )}

      {categoryToDelete && (
        <div className={styles.DeleteModalOverlay}>
          <div
            className={styles.DeleteModal}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="delete-category-title"
            aria-describedby="delete-category-description"
          >
            <div className={styles.DeleteModalIcon}>!</div>

            <h2 id="delete-category-title">Archivar categoría</h2>

            <p id="delete-category-description">
              ¿Deseas archivar la categoría{" "}
              <strong>{categoryToDelete.name}</strong>?
            </p>

            <p className={styles.DeleteModalWarning}>
              La categoría dejará de mostrarse en el listado y no podrá
              utilizarse para nuevos productos. No es posible archivar una
              categoría con subcategorías o productos activos.
            </p>

            <div className={styles.DeleteModalActions}>
              <button
                type="button"
                className={styles.CancelDeleteButton}
                onClick={handleCloseDeleteModal}
                disabled={isDeleting}
                autoFocus
              >
                Cancelar
              </button>

              <button
                type="button"
                className={styles.ConfirmDeleteButton}
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting
                  ? "Archivando..."
                  : "Archivar categoría"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}